/**
 * Sponsor Terms Display Component
 * Shows sponsor program terms, incentive structures, and payment schedules
 * Trust Marketplace - Phase 4: Network Management System
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';
import {
  FileText,
  DollarSign,
  Calendar,
  Award,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Download,
  Eye,
  Target,
  Plus,
  Edit,
  Save,
  X
} from 'lucide-react';
import { useTabbedChrome } from '@/components/weavein';

interface SponsorTermsProps {
  sponsorId?: number;
  sponsorName?: string;
}

interface SponsorProgramTerms {
  id: number;
  sponsor_id: number;
  terms_name: string;
  terms_description?: string;
  effective_date: string;
  expiration_date?: string;
  base_enrollment_incentive: number;
  payment_schedule: string;
  payment_terms?: string;
  milestone_bonus_structure?: {
    milestones?: Array<{
      name: string;
      target: number;
      bonus_amount: number;
      description?: string;
    }>;
  };
  performance_tiers?: {
    tiers?: Array<{
      tier_name: string;
      min_enrollments: number;
      max_enrollments?: number;
      incentive_multiplier: number;
      bonus_amount?: number;
    }>;
  };
  eligible_programs?: string[];
  geographic_scope?: string;
  minimum_member_requirements?: number;
  quality_metrics_required?: string[];
  reporting_frequency?: string;
  payment_method?: string;
  terms_status: string;
  created_at: string;
  updated_at: string;
}

const SponsorTerms: React.FC<SponsorTermsProps> = ({ sponsorId, sponsorName }) => {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const { fmt } = useFormattedDate();
  const [terms, setTerms] = useState<SponsorProgramTerms[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTerms, setSelectedTerms] = useState<SponsorProgramTerms | null>(null);

  // Form state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingTermsId, setEditingTermsId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    terms_name: '',
    terms_description: '',
    program_id: '',
    base_enrollment_incentive: '0.00',
    per_member_rate: '',
    per_activity_rate: '',
    payment_schedule: 'MONTHLY',
    payment_method: 'DIRECT_DEPOSIT',
    payment_terms_days: '30',
    minimum_payout_threshold: '10.00',
    effective_date: '',
    expiration_date: '',
    auto_renew: false,
    status: 'DRAFT'
  });
  const [formSubmitting, setFormSubmitting] = useState(false);

  useEffect(() => {
    if (sponsorId) {
      fetchTerms();
    } else {
      setError('No sponsor selected. This feature requires a sponsor context.');
      setLoading(false);
    }
  }, [sponsorId]);

  const fetchTerms = async () => {
    try {
      setLoading(true);
      const url = getWeaveinApiUrl(`/api/sponsors/${sponsorId}/terms`);

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch terms: ${response.status}`);
      }

      const data = await response.json();
      setTerms(data);
    } catch (err) {
      console.error('Error fetching sponsor terms:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateForm = () => {
    setIsEditing(false);
    setEditingTermsId(null);
    setFormData({
      terms_name: '',
      terms_description: '',
      program_id: '',
      base_enrollment_incentive: '0.00',
      per_member_rate: '',
      per_activity_rate: '',
      payment_schedule: 'MONTHLY',
      payment_method: 'DIRECT_DEPOSIT',
      payment_terms_days: '30',
      minimum_payout_threshold: '10.00',
      effective_date: '',
      expiration_date: '',
      auto_renew: false,
      status: 'DRAFT'
    });
    setIsFormOpen(true);
  };

  const handleOpenEditForm = (termItem: SponsorProgramTerms) => {
    setIsEditing(true);
    setEditingTermsId(termItem.id);
    setFormData({
      terms_name: termItem.terms_name,
      terms_description: termItem.terms_description || '',
      program_id: termItem.eligible_programs?.[0] || '',
      base_enrollment_incentive: termItem.base_enrollment_incentive.toString(),
      per_member_rate: termItem.performance_tiers?.tiers?.[0]?.incentive_multiplier?.toString() || '',
      per_activity_rate: '',
      payment_schedule: termItem.payment_schedule,
      payment_method: termItem.payment_method || 'DIRECT_DEPOSIT',
      payment_terms_days: termItem.payment_terms?.match(/\d+/)?.[0] || '30',
      minimum_payout_threshold: '10.00',
      effective_date: termItem.effective_date || '',
      expiration_date: termItem.expiration_date || '',
      auto_renew: false,
      status: termItem.terms_status
    });
    setIsFormOpen(true);
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitForm = async () => {
    if (!sponsorId) return;

    try {
      setFormSubmitting(true);

      // Build the request payload
      const payload: any = {
        terms_name: formData.terms_name,
        terms_description: formData.terms_description || undefined,
        program_id: formData.program_id ? parseInt(formData.program_id) : undefined,
        base_enrollment_incentive: parseFloat(formData.base_enrollment_incentive),
        per_member_rate: formData.per_member_rate ? parseFloat(formData.per_member_rate) : undefined,
        per_activity_rate: formData.per_activity_rate ? parseFloat(formData.per_activity_rate) : undefined,
        payment_schedule: formData.payment_schedule,
        payment_method: formData.payment_method,
        payment_terms_days: parseInt(formData.payment_terms_days),
        minimum_payout_threshold: parseFloat(formData.minimum_payout_threshold),
        effective_date: formData.effective_date || undefined,
        expiration_date: formData.expiration_date || undefined,
        auto_renew: formData.auto_renew
      };

      if (isEditing) {
        payload.status = formData.status;
      }

      const url = isEditing
        ? getWeaveinApiUrl(`/api/sponsors/${sponsorId}/terms/${editingTermsId}`)
        : getWeaveinApiUrl(`/api/sponsors/${sponsorId}/terms`);

      const response = await authFetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        jsonBody: payload
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to ${isEditing ? 'update' : 'create'} terms`);
      }

      // Refresh the terms list
      await fetchTerms();
      setIsFormOpen(false);
    } catch (err) {
      console.error('Error submitting terms:', err);
      alert(err instanceof Error ? err.message : 'Failed to save terms');
    } finally {
      setFormSubmitting(false);
    }
  };

  const getStatusColor = (status: string): string => {
    if (!status) return 'bg-gray-100 text-gray-800 border-gray-200';
    switch (status.toUpperCase()) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'EXPIRED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return fmt.long(dateString);
  };

  const isTermsExpired = (expirationDate?: string): boolean => {
    if (!expirationDate) return false;
    return new Date(expirationDate) < new Date();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-12 text-center bg-yellow-50 border-yellow-200">
        <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('sponsors.sponsor_context_required', 'Sponsor Context Required')}</h3>
        <p className="text-sm text-gray-600 mb-4">
          {error}
        </p>
        <p className="text-xs text-gray-500">
          {t('sponsors.this_page_is_designed_to', 'This page is designed to show sponsor-specific terms. Please access it from a sponsor dashboard or program context.')}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Guidance Section */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">{t('sponsors.about_incentive_terms', 'About Incentive Terms')}</h3>
            <p className="text-sm text-blue-800 mb-2">
              Incentive Terms define the payment structure between you (the sponsor) and CBOs participating in your programs.
              Each program should have one set of terms that specify enrollment incentives, milestone bonuses, and payment schedules.
            </p>
            <div className="text-sm text-blue-700">
              <strong>{t('sponsors.how_terms_are_created', 'How terms are created:')}</strong>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Terms are automatically created during the Program Setup Wizard (Step 5: Terms)</li>
                <li>{t('sponsors.you_can_edit_terms_here', 'You can edit terms here after a program is created')}</li>
                <li>{t('sponsors.each_program_should_have_exactly', 'Each program should have exactly one active terms entry')}</li>
              </ul>
            </div>
          </div>
        </div>
      </Card>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {!tabbed && <h2 className="text-2xl font-bold text-gray-900">{t('sponsors.sponsor_incentive_terms', 'Sponsor Incentive Terms')}</h2>}
          {sponsorName && (
            <p className="text-sm text-gray-600 mt-1">{sponsorName}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-sm">
            {terms.length} Term{terms.length !== 1 ? 's' : ''}
          </Badge>
          <Button onClick={handleOpenCreateForm}>
            <Plus className="w-4 h-4 mr-2" />
            {t('sponsors.create_terms', 'Create Terms')}
          </Button>
        </div>
      </div>

      {/* Terms List */}
      {terms.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('sponsors.no_terms_defined', 'No terms defined')}</h3>
          <p className="text-sm text-gray-600">
            {t('sponsors.sponsor_program_terms_will_appear', 'Sponsor program terms will appear here once they\'re created')}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {terms.map((termItem) => {
            const expired = isTermsExpired(termItem.expiration_date);

            return (
              <Card
                key={termItem.id}
                className={`p-6 hover:shadow-lg transition-shadow ${expired ? 'opacity-60' : ''}`}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <h3 className="text-lg font-semibold text-gray-900">
                        {termItem.terms_name}
                      </h3>
                      {expired && (
                        <AlertCircle className="w-4 h-4 text-red-600" />
                      )}
                    </div>
                    {termItem.terms_description && (
                      <p className="text-sm text-gray-600 ml-8">
                        {termItem.terms_description}
                      </p>
                    )}
                  </div>
                  <Badge className={getStatusColor(termItem.terms_status)}>
                    {termItem.terms_status || 'UNKNOWN'}
                  </Badge>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    <div>
                      <div className="text-xs text-green-700">{t('sponsors.base_incentive', 'Base Incentive')}</div>
                      <div className="text-lg font-bold text-green-900">
                        {formatCurrency(termItem.base_enrollment_incentive)}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    <div>
                      <div className="text-xs text-blue-700">{t('sponsors.payment_schedule', 'Payment Schedule')}</div>
                      <div className="text-sm font-semibold text-blue-900">
                        {termItem.payment_schedule}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <Calendar className="w-5 h-5 text-purple-600" />
                    <div>
                      <div className="text-xs text-purple-700">{t('sponsors.effective_date', 'Effective Date')}</div>
                      <div className="text-sm font-semibold text-purple-900">
                        {formatDate(termItem.effective_date)}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <Calendar className="w-5 h-5 text-yellow-600" />
                    <div>
                      <div className="text-xs text-yellow-700">
                        {expired ? 'Expired' : 'Expires'}
                      </div>
                      <div className={`text-sm font-semibold ${expired ? 'text-red-900' : 'text-yellow-900'}`}>
                        {termItem.expiration_date ? formatDate(termItem.expiration_date) : 'Ongoing'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 text-sm">
                  {termItem.geographic_scope && (
                    <div>
                      <span className="text-gray-500">{t('sponsors.geographic_scope', 'Geographic Scope:')}</span>
                      <span className="ml-2 font-medium text-gray-900">{termItem.geographic_scope}</span>
                    </div>
                  )}

                  {termItem.minimum_member_requirements && (
                    <div>
                      <span className="text-gray-500">{t('sponsors.min_members', 'Min. Members:')}</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {termItem.minimum_member_requirements}
                      </span>
                    </div>
                  )}

                  {termItem.reporting_frequency && (
                    <div>
                      <span className="text-gray-500">{t('sponsors.reporting', 'Reporting:')}</span>
                      <span className="ml-2 font-medium text-gray-900">{termItem.reporting_frequency}</span>
                    </div>
                  )}
                </div>

                {/* Performance Tiers Preview */}
                {termItem.performance_tiers?.tiers && termItem.performance_tiers.tiers.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-semibold text-gray-900">{t('sponsors.performance_tiers', 'Performance Tiers')}</span>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {termItem.performance_tiers.tiers.slice(0, 3).map((tier, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {tier.tier_name}: {tier.incentive_multiplier}x multiplier
                        </Badge>
                      ))}
                      {termItem.performance_tiers.tiers.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{termItem.performance_tiers.tiers.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Milestone Bonuses Preview */}
                {termItem.milestone_bonus_structure?.milestones && termItem.milestone_bonus_structure.milestones.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Award className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-semibold text-gray-900">{t('sponsors.milestone_bonuses', 'Milestone Bonuses')}</span>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {termItem.milestone_bonus_structure.milestones.slice(0, 3).map((milestone, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {milestone.name}: {formatCurrency(milestone.bonus_amount)}
                        </Badge>
                      ))}
                      {termItem.milestone_bonus_structure.milestones.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{termItem.milestone_bonus_structure.milestones.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => handleOpenEditForm(termItem)}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    {t('sponsors.edit', 'Edit')}
                  </Button>

                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        onClick={() => setSelectedTerms(termItem)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        {t('sponsors.view_full_terms', 'View Full Terms')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>{termItem.terms_name}</DialogTitle>
                      </DialogHeader>

                      {selectedTerms && selectedTerms.id === termItem.id && (
                        <div className="space-y-6 mt-4">
                          {/* Full Description */}
                          {selectedTerms.terms_description && (
                            <div>
                              <h3 className="font-semibold text-gray-900 mb-2">{t('sponsors.description', 'Description')}</h3>
                              <p className="text-sm text-gray-600">{selectedTerms.terms_description}</p>
                            </div>
                          )}

                          {/* Payment Details */}
                          <div>
                            <h3 className="font-semibold text-gray-900 mb-3">{t('sponsors.payment_structure', 'Payment Structure')}</h3>
                            <div className="grid grid-cols-2 gap-4">
                              <Card className="p-3">
                                <div className="text-xs text-gray-500 mb-1">{t('sponsors.base_enrollment_incentive', 'Base Enrollment Incentive')}</div>
                                <div className="text-xl font-bold text-green-600">
                                  {formatCurrency(selectedTerms.base_enrollment_incentive)}
                                </div>
                              </Card>
                              <Card className="p-3">
                                <div className="text-xs text-gray-500 mb-1">{t('sponsors.payment_schedule_1', 'Payment Schedule')}</div>
                                <div className="text-lg font-semibold text-gray-900">
                                  {selectedTerms.payment_schedule}
                                </div>
                              </Card>
                            </div>
                            {selectedTerms.payment_terms && (
                              <p className="text-sm text-gray-600 mt-3">{selectedTerms.payment_terms}</p>
                            )}
                          </div>

                          {/* Performance Tiers */}
                          {selectedTerms.performance_tiers?.tiers && selectedTerms.performance_tiers.tiers.length > 0 && (
                            <div>
                              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-blue-600" />
                                {t('sponsors.performance_tiers_1', 'Performance Tiers')}
                              </h3>
                              <div className="space-y-2">
                                {selectedTerms.performance_tiers.tiers.map((tier, index) => (
                                  <Card key={index} className="p-4 bg-blue-50 border-blue-200">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <div className="font-semibold text-gray-900">{tier.tier_name}</div>
                                        <div className="text-sm text-gray-600">
                                          {tier.min_enrollments}
                                          {tier.max_enrollments ? ` - ${tier.max_enrollments}` : '+'} enrollments
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <div className="text-lg font-bold text-blue-600">
                                          {tier.incentive_multiplier}x
                                        </div>
                                        {tier.bonus_amount && (
                                          <div className="text-sm text-green-600">
                                            +{formatCurrency(tier.bonus_amount)} bonus
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </Card>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Milestone Bonuses */}
                          {selectedTerms.milestone_bonus_structure?.milestones && selectedTerms.milestone_bonus_structure.milestones.length > 0 && (
                            <div>
                              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <Award className="w-5 h-5 text-purple-600" />
                                {t('sponsors.milestone_bonuses_1', 'Milestone Bonuses')}
                              </h3>
                              <div className="space-y-2">
                                {selectedTerms.milestone_bonus_structure.milestones.map((milestone, index) => (
                                  <Card key={index} className="p-4 bg-purple-50 border-purple-200">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <div className="font-semibold text-gray-900">{milestone.name}</div>
                                        <div className="text-sm text-gray-600">
                                          Target: {milestone.target.toLocaleString()}
                                        </div>
                                        {milestone.description && (
                                          <div className="text-xs text-gray-500 mt-1">{milestone.description}</div>
                                        )}
                                      </div>
                                      <div className="text-lg font-bold text-purple-600">
                                        {formatCurrency(milestone.bonus_amount)}
                                      </div>
                                    </div>
                                  </Card>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Requirements */}
                          <div>
                            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                              <CheckCircle className="w-5 h-5 text-green-600" />
                              {t('sponsors.requirements', 'Requirements')}
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                              {selectedTerms.minimum_member_requirements && (
                                <div className="text-sm">
                                  <span className="text-gray-500">{t('sponsors.minimum_members', 'Minimum Members:')}</span>
                                  <span className="ml-2 font-medium text-gray-900">
                                    {selectedTerms.minimum_member_requirements}
                                  </span>
                                </div>
                              )}
                              {selectedTerms.geographic_scope && (
                                <div className="text-sm">
                                  <span className="text-gray-500">{t('sponsors.geographic_scope_1', 'Geographic Scope:')}</span>
                                  <span className="ml-2 font-medium text-gray-900">
                                    {selectedTerms.geographic_scope}
                                  </span>
                                </div>
                              )}
                              {selectedTerms.reporting_frequency && (
                                <div className="text-sm">
                                  <span className="text-gray-500">{t('sponsors.reporting_frequency', 'Reporting Frequency:')}</span>
                                  <span className="ml-2 font-medium text-gray-900">
                                    {selectedTerms.reporting_frequency}
                                  </span>
                                </div>
                              )}
                              {selectedTerms.payment_method && (
                                <div className="text-sm">
                                  <span className="text-gray-500">{t('sponsors.payment_method', 'Payment Method:')}</span>
                                  <span className="ml-2 font-medium text-gray-900">
                                    {selectedTerms.payment_method}
                                  </span>
                                </div>
                              )}
                            </div>

                            {selectedTerms.quality_metrics_required && selectedTerms.quality_metrics_required.length > 0 && (
                              <div className="mt-3">
                                <div className="text-sm text-gray-500 mb-2">{t('sponsors.quality_metrics_required', 'Quality Metrics Required:')}</div>
                                <div className="flex flex-wrap gap-2">
                                  {selectedTerms.quality_metrics_required.map((metric, index) => (
                                    <Badge key={index} variant="outline" className="text-xs">
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      {metric}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {selectedTerms.eligible_programs && selectedTerms.eligible_programs.length > 0 && (
                              <div className="mt-3">
                                <div className="text-sm text-gray-500 mb-2">{t('sponsors.eligible_programs', 'Eligible Programs:')}</div>
                                <div className="flex flex-wrap gap-2">
                                  {selectedTerms.eligible_programs.map((program, index) => (
                                    <Badge key={index} variant="outline" className="text-xs">
                                      <Target className="w-3 h-3 mr-1" />
                                      {program}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>

                  <Button variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    {t('sponsors.export', 'Export')}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isEditing ? 'Edit Program Terms' : 'Create New Program Terms'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Basic Information */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('sponsors.basic_information', 'Basic Information')}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('sponsors.terms_name', 'Terms Name')} <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.terms_name}
                    onChange={(e) => handleFormChange('terms_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Standard CBO Incentive Terms 2025"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('sponsors.description_1', 'Description')}
                  </label>
                  <textarea
                    value={formData.terms_description}
                    onChange={(e) => handleFormChange('terms_description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    placeholder={t('sponsors.describe_the_terms_and_conditions', 'Describe the terms and conditions...')}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Program ID (Optional)
                    </label>
                    <input
                      type="number"
                      value={formData.program_id}
                      onChange={(e) => handleFormChange('program_id', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={t('sponsors.link_to_specific_program', 'Link to specific program')}
                    />
                  </div>

                  {isEditing && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('sponsors.status', 'Status')}
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => handleFormChange('status', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="DRAFT">{t('sponsors.draft', 'Draft')}</option>
                        <option value="ACTIVE">{t('sponsors.active', 'Active')}</option>
                        <option value="SUSPENDED">{t('sponsors.suspended', 'Suspended')}</option>
                        <option value="EXPIRED">{t('sponsors.expired', 'Expired')}</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Incentive Structure */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('sponsors.incentive_structure', 'Incentive Structure')}</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('sponsors.base_enrollment_incentive_1', 'Base Enrollment Incentive')} <span className="text-red-600">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.base_enrollment_incentive}
                      onChange={(e) => handleFormChange('base_enrollment_incentive', e.target.value)}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('sponsors.per_member_rate', 'Per Member Rate')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.per_member_rate}
                      onChange={(e) => handleFormChange('per_member_rate', e.target.value)}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('sponsors.per_activity_rate', 'Per Activity Rate')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.per_activity_rate}
                      onChange={(e) => handleFormChange('per_activity_rate', e.target.value)}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Terms */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('sponsors.payment_terms', 'Payment Terms')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('sponsors.payment_schedule_2', 'Payment Schedule')}
                  </label>
                  <select
                    value={formData.payment_schedule}
                    onChange={(e) => handleFormChange('payment_schedule', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="WEEKLY">{t('sponsors.weekly', 'Weekly')}</option>
                    <option value="BIWEEKLY">{t('sponsors.biweekly', 'Bi-weekly')}</option>
                    <option value="MONTHLY">{t('sponsors.monthly', 'Monthly')}</option>
                    <option value="QUARTERLY">{t('sponsors.quarterly', 'Quarterly')}</option>
                    <option value="MILESTONE">{t('sponsors.per_milestone', 'Per Milestone')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('sponsors.payment_method_1', 'Payment Method')}
                  </label>
                  <select
                    value={formData.payment_method}
                    onChange={(e) => handleFormChange('payment_method', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="DIRECT_DEPOSIT">{t('sponsors.direct_deposit', 'Direct Deposit')}</option>
                    <option value="CHECK">{t('sponsors.check', 'Check')}</option>
                    <option value="WIRE">{t('sponsors.wire_transfer', 'Wire Transfer')}</option>
                    <option value="ACH">ACH</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('sponsors.payment_terms_days', 'Payment Terms (Days)')}
                  </label>
                  <input
                    type="number"
                    value={formData.payment_terms_days}
                    onChange={(e) => handleFormChange('payment_terms_days', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="30"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('sponsors.minimum_payout_threshold', 'Minimum Payout Threshold')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.minimum_payout_threshold}
                      onChange={(e) => handleFormChange('minimum_payout_threshold', e.target.value)}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="10.00"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Effective Dates */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('sponsors.effective_period', 'Effective Period')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('sponsors.effective_date_1', 'Effective Date')}
                  </label>
                  <input
                    type="date"
                    value={formData.effective_date}
                    onChange={(e) => handleFormChange('effective_date', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('sponsors.expiration_date', 'Expiration Date')}
                  </label>
                  <input
                    type="date"
                    value={formData.expiration_date}
                    onChange={(e) => handleFormChange('expiration_date', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="mt-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.auto_renew}
                    onChange={(e) => handleFormChange('auto_renew', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{t('sponsors.autorenew_terms', 'Auto-renew terms')}</span>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setIsFormOpen(false)}
                disabled={formSubmitting}
              >
                <X className="w-4 h-4 mr-2" />
                {t('sponsors.cancel', 'Cancel')}
              </Button>
              <Button
                onClick={handleSubmitForm}
                disabled={formSubmitting || !formData.terms_name}
              >
                {formSubmitting ? (
                  <>
                    <LoadingSpinner />
                    <span className="ml-2">{t('sponsors.saving', 'Saving...')}</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {isEditing ? 'Update Terms' : 'Create Terms'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SponsorTerms;
